import type { ReactNode } from 'react'
import { Alert, Form, Input, Modal, Space } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { REASON_MAX } from './cashdeskApi'

export interface CashDecision {
  title: string
  okText: string
  danger: boolean
  warning: string
  /** `true` — sabab majburiy (rad etish, qaytarish); `false` — ixtiyoriy izoh (tasdiqlash) */
  reasonRequired: boolean
  details: ReactNode
  submit: (text: string | null) => Promise<unknown>
}

interface CashDecisionModalProps {
  decision: CashDecision | null
  onClose: () => void
  onDone: () => void
}

export function CashDecisionModal({ decision, onClose, onDone }: CashDecisionModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ text?: string }>()

  const run = useMutation({
    mutationFn: (text: string | null) => (decision ? decision.submit(text) : Promise.resolve(null)),
    onSuccess: () => {
      form.resetFields()
      onDone()
    },
  })

  const close = () => {
    form.resetFields()
    run.reset()
    onClose()
  }

  return (
    <Modal
      open={decision !== null}
      title={decision?.title}
      okText={decision?.okText}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: decision?.danger ?? false }}
      confirmLoading={run.isPending}
      onCancel={close}
      destroyOnHidden
      onOk={() =>
        submitOnValid(form, (values) => {
          const text = values.text?.trim() ?? ''
          run.mutate(text === '' ? null : text)
        })
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {decision ? <Alert type="warning" showIcon message={decision.warning} /> : null}
        {decision?.details}
        {run.isError ? <Alert type="error" showIcon message={translateError(run.error, t)} /> : null}
        <Form form={form} layout="vertical" requiredMark preserve={false}>
          <Form.Item
            name="text"
            label={decision?.reasonRequired ? t('actions.reason') : t('cashdesk.decision.note')}
            rules={[
              ...(decision?.reasonRequired
                ? [{ required: true, whitespace: true, message: t('cashdesk.decision.reasonRequired') }]
                : []),
              { max: REASON_MAX, message: t('cashdesk.reasonTooLong') },
            ]}
          >
            <Input.TextArea rows={3} maxLength={REASON_MAX} showCount />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}
